'use client';

import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import DaemonTriggerDetailModal from '../DaemonTriggerDetailModal';
import DaemonTriggerDetailPanel from '../DaemonTriggerDetailPanel';
import { prepareUserConventionOptionsForPicker } from '../UserConventionSelector';
import { useIsMdUp } from '../../hooks/useIsMdUp';
import { useIsDesktopApp } from '../../hooks/useIsDesktopApp';
import { useAgentConfigs } from '../../hooks/queries/useAgentConfigs';
import { useDaemons } from '../../hooks/queries/useDaemons';
import { useConventions } from '../../hooks/queries/useConventions';
import {
  useActiveDaemonTriggerCount,
  useCancelDaemonTriggerMutation,
  useContinueDaemonTriggerMutation,
  useCreateDaemonTriggerMutation,
  useDaemonTriggerById,
  useDeleteDaemonTriggerMutation,
} from '../../hooks/queries/useDaemonTriggers';
import { useTriggerReviewMutation } from '../../hooks/queries/useTriggerReview';
import { useDiscoveredWorktrees, useWorktreeList } from '../../hooks/useWorktreeList';
import { resolveApiErrorToastMessageOrFallback } from '../../lib/api/localizeApiError';
import {
  activeConcurrencyWarningReasonFromVerdict,
  buildCheckoutIdByAgentConfigId,
  type ActiveTriggerConflictResult,
  evaluateActiveTriggerConflict,
} from '../../lib/daemonTriggerConflict';
import type { DaemonTrigger, MeActiveRun } from '../../lib/api';
import { resolveDaemonTriggerRunnerBoxInfo } from '../daemonTriggerRunnerBox';

type DaemonTriggerDetailRunTarget = Pick<MeActiveRun, 'id' | 'projectId'>;

type DaemonTriggerDetailModalConnectedProps = {
  run: DaemonTriggerDetailRunTarget | null;
  token: string | null;
  memberId: string | null;
  onClose: () => void;
  /**
   * 글로벌 continue 모달의 source trigger id. 같은 trigger를 detail로 보고 있을 때 안내 배너와
   * 복귀 버튼을 노출해 작성 중인 draft를 잃지 않도록 한다.
   */
  activeContinueDraftTriggerId?: string | null;
};

export default function DaemonTriggerDetailModalConnected({
  run,
  token,
  memberId,
  onClose,
  activeContinueDraftTriggerId = null,
}: DaemonTriggerDetailModalConnectedProps) {
  const t = useTranslations('daemonTriggers.page');
  const tRoot = useTranslations();
  const tLayout = useTranslations('components.appLayout');
  const isMdUp = useIsMdUp();
  const isDesktopApp = useIsDesktopApp();
  const showPanel = isMdUp || isDesktopApp;

  const triggerId = run?.id ?? null;
  const projectId = run?.projectId ?? '';

  const daemonTriggerQuery = useDaemonTriggerById(token, triggerId);
  const trigger: DaemonTrigger | null = daemonTriggerQuery.data ?? null;

  const { data: agentConfigs = [] } = useAgentConfigs(token, projectId);
  const { data: daemonsResult } = useDaemons(token, { enabled: Boolean(run) });
  const daemons = useMemo(() => daemonsResult?.daemons ?? [], [daemonsResult?.daemons]);
  const { data: projectConventionsForUserPicker = [] } = useConventions(token, projectId, { pageSize: 100 });
  const { data: worktreeListData = [] } = useWorktreeList(token, projectId, true);
  const { data: discoveredWorktrees = [] } = useDiscoveredWorktrees(token, projectId);

  const { data: activeDaemonTriggers = [] } = useActiveDaemonTriggerCount(token, projectId);

  const deleteDaemonTriggerMutation = useDeleteDaemonTriggerMutation(token);
  const cancelDaemonTriggerMutation = useCancelDaemonTriggerMutation(token, triggerId);
  const continueDaemonTriggerMutation = useContinueDaemonTriggerMutation(token, triggerId);
  const createDaemonTriggerMutation = useCreateDaemonTriggerMutation(token);
  const triggerReviewMutation = useTriggerReviewMutation(token);

  const agentConfig = useMemo(
    () => (trigger ? agentConfigs.find((c) => c.id === trigger.agentConfigId) : undefined),
    [agentConfigs, trigger],
  );

  const daemonInfo = useMemo(() => {
    if (!trigger) return null;
    const daemonId = trigger.targetDaemonId ?? trigger.claimedByDaemonId;
    if (!daemonId) return null;
    const daemon = daemons.find((d) => d.id === daemonId);
    if (!daemon) return null;
    const label = daemon.label?.trim() ? daemon.label : daemon.id.slice(0, 8);
    return { label, osType: daemon.osType ?? null };
  }, [daemons, trigger]);

  const userConventionOptions = useMemo(
    () =>
      prepareUserConventionOptionsForPicker(
        projectConventionsForUserPicker.map((c) => ({
          id: c.id,
          title: c.title,
          category: c.category,
          contentTokenCount: c.contentTokenCount ?? null,
          runnerInjectedCount: c.runnerInjectedCount,
          trigger: c.trigger,
          updatedAt: c.updatedAt,
        })),
      ),
    [projectConventionsForUserPicker],
  );

  const runnerBoxInfo = useMemo(
    () => resolveDaemonTriggerRunnerBoxInfo(trigger, worktreeListData, discoveredWorktrees),
    [discoveredWorktrees, trigger, worktreeListData],
  );

  const activeConcurrencyConflict = useMemo<ActiveTriggerConflictResult>(() => {
    if (!trigger) return { verdict: 'NONE', reason: null };
    const daemonId = trigger.targetDaemonId ?? trigger.claimedByDaemonId ?? '';
    return evaluateActiveTriggerConflict({
      agentConfigId: trigger.agentConfigId ?? '',
      daemonId,
      planMode: Boolean(trigger.planMode),
      // 이어쓰기는 원본의 작업 디렉터리를 그대로 상속한다.
      useWorktree: Boolean(trigger.useWorktree),
      worktreeId: trigger.worktreeId ?? null,
      discoveredWorktreeId: trigger.discoveredWorktreeId ?? null,
      activeTriggers: activeDaemonTriggers,
      checkoutIdByAgentConfigId: buildCheckoutIdByAgentConfigId(agentConfigs, daemonId),
      excludeTriggerId: trigger.id,
    });
  }, [activeDaemonTriggers, agentConfigs, trigger]);

  const activeConcurrencyWarningReason = activeConcurrencyWarningReasonFromVerdict(activeConcurrencyConflict);

  const chainTokenCount = trigger?.linkedCoAction?.chainTotalTokenCount ?? null;

  const handleDeleteTrigger = async (target: DaemonTrigger) => {
    if (target.status !== 'PENDING') return;
    try {
      await deleteDaemonTriggerMutation.mutateAsync(target.id);
      onClose();
      toast.success(t('deleteSuccess'));
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('deleteFailed'));
      if (message) toast.error(message);
    }
  };

  const handleCancelTrigger = async (): Promise<DaemonTrigger> => {
    try {
      const cancelled = await cancelDaemonTriggerMutation.mutateAsync();
      toast.success(t('cancelSuccess'));
      return cancelled;
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('cancelFailed'));
      if (message) toast.error(message);
      throw error;
    }
  };

  const handleContinueAction = async (
    prompt: object,
    overrides?: {
      runnerType?: string;
      model?: string | null;
      fastMode?: boolean;
      planMode?: boolean;
      userConventionIds?: string[];
      attachmentDrafts?: Array<{ key: string; originalName: string }>;
    },
  ): Promise<DaemonTrigger> => {
    try {
      const continued = await continueDaemonTriggerMutation.mutateAsync({ prompt, ...overrides });
      toast.success(tLayout('daemonTriggerContinueSent'));
      onClose();
      return continued;
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, tLayout('daemonTriggerContinueFailed'));
      if (message) toast.error(message);
      throw error;
    }
  };

  const handleNewRequestWithWorktree = async (prompt: object): Promise<DaemonTrigger> => {
    if (!trigger) throw new Error('No trigger selected');
    try {
      const newTrigger = await createDaemonTriggerMutation.mutateAsync({
        prompt,
        agentConfigId: trigger.agentConfigId,
        targetDaemonId: trigger.targetDaemonId ?? trigger.claimedByDaemonId ?? undefined,
        runnerType: trigger.runnerType,
        model: trigger.model ?? undefined,
        planMode: trigger.planMode,
        useWorktree: true,
        repositoryId: trigger.repositoryId ?? undefined,
        baseBranch: trigger.baseBranch ?? undefined,
        worktreeId: trigger.worktreeId ?? undefined,
      });
      toast.success(t('newRequestSent'));
      return newTrigger;
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('newRequestFailed'));
      if (message) toast.error(message);
      throw error;
    }
  };

  const handleReviewTrigger = async (decision: 'approve' | 'reject'): Promise<void> => {
    if (!trigger) return;
    try {
      await triggerReviewMutation.mutateAsync({ triggerId: trigger.id, decision });
      onClose();
      toast.success(decision === 'approve' ? 'Trigger approved.' : 'Trigger rejected.');
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, 'Failed to review trigger.');
      if (message) toast.error(message);
    }
  };

  const isOpen = Boolean(run);

  useEffect(() => {
    const dispatch = (open: boolean) => {
      window.dispatchEvent(
        new CustomEvent('daemonTriggerDetailPanelVisibilityChanged', {
          detail: { isOpen: open },
        }),
      );
    };
    dispatch(isOpen);
    return () => dispatch(false);
  }, [isOpen]);

  const detailProps = {
    trigger,
    isOpen,
    onClose,
    isLoading: daemonTriggerQuery.isLoading,
    agentName: agentConfig?.name,
    agentOsType: agentConfig?.osType ?? null,
    daemonName: daemonInfo?.label,
    daemonOsType: daemonInfo?.osType,
    isDeleting: deleteDaemonTriggerMutation.isPending,
    onDeleteAction: (target: DaemonTrigger) => void handleDeleteTrigger(target),
    onCancelAction: handleCancelTrigger,
    isCancelPending: cancelDaemonTriggerMutation.isPending,
    onContinueAction: handleContinueAction,
    isContinuePending: continueDaemonTriggerMutation.isPending,
    isContinuePlanModeForced: agentConfig?.planModeOnly === true,
    chainLength: 1,
    chainTokenCount,
    onNewRequestWithWorktreeAction: handleNewRequestWithWorktree,
    isNewRequestPending: createDaemonTriggerMutation.isPending,
    activeConcurrencyWarningReason,
    runnerBoxInfo,
    userConventionOptions,
    onReviewAction: handleReviewTrigger,
    isReviewPending: triggerReviewMutation.isPending,
    presetContext: { token, projectId, memberId },
    activeContinueDraftTriggerId,
  };

  return showPanel ? <DaemonTriggerDetailPanel {...detailProps} /> : <DaemonTriggerDetailModal {...detailProps} />;
}
